import { SEARCH_ENGINES } from '../constants';
import type { BrowserSettings, RenderMode, SearchProvider } from '../types';

interface Props {
  settings: BrowserSettings;
  onChange: (settings: BrowserSettings) => void;
  offlineCount: number;
}

export function SettingsPage({ settings, onChange, offlineCount }: Props) {
  const update = <K extends keyof BrowserSettings>(key: K, value: BrowserSettings[K]) => onChange({ ...settings, [key]: value });

  function setSearchProvider(provider: SearchProvider) {
    onChange({
      ...settings,
      searchProvider: provider,
      searchEngine: provider === 'custom' ? settings.searchEngine : SEARCH_ENGINES[provider].template
    });
  }

  return (
    <section className="internal-page settings-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">websurf://settings</p>
          <h1>Settings</h1>
        </div>
        <span>{offlineCount} offline page(s) saved</span>
      </header>

      <div className="settings-section search-settings-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Search Browser</p>
            <h2>Default search engine</h2>
          </div>
          <span className="pill">Current: {SEARCH_ENGINES[settings.searchProvider]?.label || 'Custom'}</span>
        </div>
        <div className="search-provider-grid">
          {(Object.keys(SEARCH_ENGINES) as SearchProvider[]).map((provider) => (
            <button
              key={provider}
              type="button"
              className={settings.searchProvider === provider ? 'provider-card active' : 'provider-card'}
              onClick={() => setSearchProvider(provider)}
            >
              <strong>{SEARCH_ENGINES[provider].label}</strong>
              <span>{SEARCH_ENGINES[provider].note}</span>
            </button>
          ))}
        </div>
        {settings.searchProvider === 'custom' && (
          <label className="full-field">
            <span>Custom search URL template</span>
            <input value={settings.searchEngine} onChange={(event) => update('searchEngine', event.target.value)} placeholder="https://example.com/search?q=%s" />
            <small>Include <code>%s</code> where the search text should be inserted.</small>
          </label>
        )}
      </div>

      <div className="settings-grid">
        <label><span>Homepage</span><input value={settings.homepage} onChange={(event) => update('homepage', event.target.value)} /></label>
        <label><span>Proxy timeout (ms)</span><input type="number" min="5000" max="180000" step="1000" value={settings.proxyTimeoutMs} onChange={(event) => update('proxyTimeoutMs', Number(event.target.value))} /></label>
        <label><span>Translate target</span><select value={settings.translateTarget} onChange={(event) => update('translateTarget', event.target.value)}><option value="en">English</option><option value="es">Spanish</option><option value="fr">French</option><option value="de">German</option><option value="ar">Arabic</option></select></label>
        <label className="check"><input type="checkbox" checked={settings.openHomepageOnNewTab} onChange={(event) => update('openHomepageOnNewTab', event.target.checked)} /> Open homepage on new tab</label>
        <label className="check"><input type="checkbox" checked={settings.showBookmarksBar} onChange={(event) => update('showBookmarksBar', event.target.checked)} /> Show bookmarks bar</label>
        <label className="check"><input type="checkbox" checked={settings.autoTranslate} onChange={(event) => update('autoTranslate', event.target.checked)} /> Auto-translate pages</label>
        <label className="check"><input type="checkbox" checked={settings.reducedMotion} onChange={(event) => update('reducedMotion', event.target.checked)} /> Reduce motion</label>
      </div>

      <div className="settings-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Proxy engine</p>
            <h2>Compatibility and speed</h2>
          </div>
          <span className="pill">Enhanced proxy</span>
        </div>
        <div className="settings-grid compact-grid">
          <label><span>Default render strategy</span><select value={settings.defaultRenderMode || 'smart'} onChange={(event) => update('defaultRenderMode', event.target.value as RenderMode)}><option value="smart">Smart: native launch for game pages, proxy/direct for normal sites</option><option value="native">Native: open games as top-level browser pages</option><option value="direct">Direct: fastest iframe mode when framing is allowed</option><option value="proxy">Proxy: strips frame blockers, best for blocked pages</option></select></label>
          <label className="check"><input type="checkbox" checked={settings.compatibilityMode} onChange={(event) => update('compatibilityMode', event.target.checked)} /> Enhanced compatibility mode</label>
          <label className="check"><input type="checkbox" checked={settings.proxyCacheEnabled} onChange={(event) => update('proxyCacheEnabled', event.target.checked)} /> Cache static proxy assets</label>
          <label className="check"><input type="checkbox" checked={settings.preferReadableFallback} onChange={(event) => update('preferReadableFallback', event.target.checked)} /> Prefer readable fallback on hard failures</label>
        </div>
        <p className="helper-copy">Smart rendering now uses Native Launch for game detail pages that commonly hang in proxy/iframe mode. The proxy is still available for browsing portal pages and blocked websites, and Direct Mode remains available for sites that truly allow iframe rendering. Account sign-in pages may still block embedded/proxied browsers for security reasons.</p>
      </div>

      <div className="info-card">
        <h2>Error page integration</h2>
        <p>WebSurf dynamically loads <code>offline.html</code>, <code>error.html</code>, <code>timeout.html</code>, and <code>site-unavailable.html</code> inside the tab renderer. The toolbar and tabs remain visible because these pages render in the browser pane, not as full-app navigations.</p>
      </div>
    </section>
  );
}
